import LoaderWrapper from '@flatsy/core/Loader/Wrapper'
import { FetchStatus } from '@flatsy/types/FetchStatus'
import { parseApiDate } from '@flatsy/utils/Functions/DateTime/Date'
import { formatSlotForBackEnd } from '@flatsy/utils/Functions/DateTime/Datefns'
import { unwrapResult } from '@reduxjs/toolkit'
import { Body1, Body2, SubBody2 } from '@flatsy/core/MaterialUi/Typo'
import theme from '@flatsy/core/MuiTheme/theme'
import { useApplicationDetails } from 'src/hooks/ApplicationDetails'
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined'
import clsx from 'clsx'
import { Avatar } from '@material-ui/core'
import React, { useContext, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { updatePublicBooking } from 'src/redux/slices/Booking'
import { AppDispatch } from 'src/utils/AppDispatch'
import { useSelectAsset } from 'src/hooks/Asset'
import { RootState } from 'src/redux'
import i18n from '@flatsy/utils/translations'
import HightlightSlot from './HightlightSlot'
import { BookingProps } from './Type'
import { useStyles } from './Style'
import Weeks from './Weeks'
import { FormContext, ReturnContextProps } from '../../Context'
import { renderTitleHightlightSlot, renderTitleShowWeeks } from './Utils'

const Booking: React.FC<BookingProps> = ({
  withOutApplication,
  updateBooking,
  fetchApplication,
}): React.ReactElement => {
  const {
    wizardContext: { context },
    paramsUrl,
    saveApplicationInRedux,
    saveApplicationAndContinue,
  } = useContext(FormContext) as ReturnContextProps
  const classes = useStyles()
  const formContext = useContext(FormContext)
  const { asset } = useSelectAsset()
  const [loadingAction, setLoadingAction] = useState(false)
  const [showWeeks, setShowWeeks] = useState(false)
  const dispatch: AppDispatch = useDispatch()
  const slotsStatus = useSelector<RootState, FetchStatus>((state) => state.bookableSlots.status)
  const {
    application: { application },
  } = useApplicationDetails()
  const { agent } = application

  if (!formContext) {
    return <></>
  }

  if (!asset || !application) {
    return <></>
  }

  const handleShowWeeks = () => {
    setShowWeeks(true)
  }

  const handleUpdateBooking = async (date: string, start: string) => {
    setLoadingAction(true)
    try {
      const action = await dispatch(
        updatePublicBooking({
          applicationId: paramsUrl.applicationId || '',
          start_date: date,
          start_time: start,
        })
      )
      unwrapResult(action)
      if (fetchApplication) {
        fetchApplication()
      }
      setLoadingAction(false)
    } catch (err) {
      setLoadingAction(false)
    }
  }

  const handleClickOnSlot = (start: string) => {
    const theSlot = formatSlotForBackEnd(parseApiDate(start), parseApiDate(start))
    if (updateBooking) {
      handleUpdateBooking(theSlot.date, theSlot.start)
    } else if (withOutApplication) {
      saveApplicationInRedux({
        ...context.application,
        booking_wanted_date: theSlot.date,
        booking_wanted_time: theSlot.start,
        asset: paramsUrl.id,
      })
    } else {
      saveApplicationAndContinue({
        booking_wanted_date: theSlot.date,
        booking_wanted_time: theSlot.start,
        asset: paramsUrl.id,
      })
    }
  }

  const renderAvatarAgent = () => {
    let agentToShow = agent
    if (context && context?.slots && context?.slots[0] && context?.slots[0].agent) {
      agentToShow = context.slots[0].agent
    }

    if (agentToShow) {
      return (
        <>
          <Avatar
            alt={`${agentToShow?.first_name} ${agentToShow?.last_name}`}
            src={agentToShow?.picture_url}
            className={classes.avatar}
          />
          <Body1 className={classes.agentName}>{agentToShow?.first_name} </Body1>
        </>
      )
    }
  }

  return (
    <>
      {showWeeks ? (
        <>
          <div className={classes.showWeeks}>
            {renderTitleShowWeeks(application)}
            <div className={classes.agent}>
              <SubBody2>{i18n.t('with')}</SubBody2>
              {renderAvatarAgent()}
              <Body2 className={classes.agence}>
                {i18n.t('of').toLowerCase()} {asset?.agency_name}
              </Body2>
            </div>
          </div>
          {asset?.instructions && (
            <div className={clsx(classes.instruction, classes.bottom)}>
              <ReportProblemOutlinedIcon className={classes.iconInstruction} />
              <Body2>{asset.instructions}</Body2>
            </div>
          )}
          <Weeks
            slots={context.slots}
            asset={asset}
            handleClickOnSlot={handleClickOnSlot}
            loading={slotsStatus === FetchStatus.LOADING || slotsStatus === FetchStatus.IDLE}
          />
        </>
      ) : (
        <>
          {renderTitleHightlightSlot(application, classes)}
          {asset.instructions && (
            <div className={classes.instruction}>
              <ReportProblemOutlinedIcon className={classes.iconInstruction} />
              <Body2>{asset.instructions}</Body2>
            </div>
          )}
          <Body2 fontColor={theme.palette.secondary.dark} className={classes.agent}>
            {i18n.t('below_preferred_slots_for')}
            {renderAvatarAgent()}
          </Body2>
          <HightlightSlot
            slots={context.slots}
            handleClickOnSlot={handleClickOnSlot}
            showWeeks={handleShowWeeks}
            loading={slotsStatus === FetchStatus.LOADING || slotsStatus === FetchStatus.IDLE}
          />
        </>
      )}
      {loadingAction && <LoaderWrapper absolute />}
    </>
  )
}

export default Booking
